import { useCallback, useEffect, useState } from 'react';
import {
  apiDownloadInvoiceFileAsCreator,
  apiDownloadInvoiceFileAsOffice,
} from '../../../../api';
import {
  EIncomingInvoiceState,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';

export enum InvoicePdfLoadMode {
  Office = 'office',
  Creator = 'creator',
}

export const useInvoicePdf = (
  invoice: IncomingInvoiceDto | null,
  mode: InvoicePdfLoadMode
) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const loadInvoicePdf = useCallback(async () => {
    if (!invoice?.incomingInvoiceId) return;
    if (invoice.incomingInvoiceState === EIncomingInvoiceState.Processing)
      return;

    try {
      setPdfLoading(true);
      const result =
        mode === InvoicePdfLoadMode.Office
          ? await apiDownloadInvoiceFileAsOffice(
              invoice.incomingInvoiceId,
              invoice.toOfficeId
            )
          : await apiDownloadInvoiceFileAsCreator(invoice.incomingInvoiceId);
      if (result.result.ok && result.data) {
        const pdfBlob = new Blob([result.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setPdfLoadError(false);
      } else {
        setPdfUrl(null);
        setPdfLoadError(true);
      }
    } catch {
      setPdfUrl(null);
      setPdfLoadError(true);
    } finally {
      setPdfLoading(false);
    }
  }, [invoice, mode]);

  // cleanup object url
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const resetPdf = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfLoading(false);
    setPdfLoadError(false);
  }, [pdfUrl]);

  return { pdfUrl, pdfLoading, pdfLoadError, loadInvoicePdf, resetPdf };
};
